import React, { FC, useState } from "react";
import {
  AckErrorMessage,
  AckLoader,
  AckTable,
  AckTableRowFormatter,
  AckTableRowProps,
  convertToDisplayValue,
  formatCurrency,
  ModelSpec,
  useFetch,
} from "ackbox-weblib-react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import {
  Accordion,
  COLORS,
  Empty,
  formatDecoratedCurrency,
  SUMMARIES_API_PATH,
  useEffectOnce,
} from "./Constants";
import { Progress, useProgress } from "./Progress";

const FundsSummary: FC<{ month: string }> = (props) => {
  const [rows, setRows] = useState([] as AckTableRowProps[]);
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    const value = await ackFetch.get(`funds/?within=${props.month}`);
    const spec: ModelSpec[] = [
      { key: "incomes", label: "Incomes", name: "incomes", type: "currency" },
      {
        key: "expenses",
        label: "Expenses",
        name: "expenses",
        type: "currency",
      },
      {
        key: "transfersIn",
        label: "Transfers In",
        name: "transfersIn",
        type: "currency",
      },
      {
        key: "transfersOut",
        label: "Transfers Out",
        name: "transfersOut",
        type: "currency",
      },
      { key: "total", label: "Total", name: "total", type: "currency" },
    ];
    const rows: AckTableRowProps[] = [{ spec: spec, payload: value }];

    setRows(rows);
  }, [props.month]);

  const formatters: AckTableRowFormatter = (
    spec: ModelSpec,
    payload: any,
    _: number
  ): any => {
    if (spec.name === "total") {
      return <strong>{formatDecoratedCurrency(payload[spec.name])}</strong>;
    }
    return convertToDisplayValue(spec, payload);
  };

  return (
    <>
      <h1 className="subtitle">Monthly Funds</h1>
      <AckErrorMessage errors={errors} />
      <AckLoader isLoading={isLoading} />
      {!isLoading && (
        <AckTable rows={rows} formatters={formatters} hideIndexCol={true} />
      )}
    </>
  );
};

const ExpensesBreakdown: FC<{ month: string }> = (props) => {
  const [data, setData] = useState([] as any[]);
  const [rows, setRows] = useState([] as AckTableRowProps[]);
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    const values = await ackFetch.get(
      `category-expenses/?within=${props.month}`
    );
    const data = values.map((value: any) => ({
      name: value.category,
      value: value.total,
    }));
    const spec: ModelSpec[] = [
      { key: "category", label: "Category", name: "category", type: "string" },
      { key: "total", label: "Total", name: "total", type: "currency" },
      { key: "details", label: "Details", name: "details", type: "object" },
    ];
    const rows: AckTableRowProps[] = values.map((value: any) => ({
      spec: spec,
      payload: value,
    }));

    setRows(rows);
    setData(data);
  }, [props.month]);

  const formatters: AckTableRowFormatter = (
    spec: ModelSpec,
    payload: any,
    _: number
  ): any => {
    if (spec.name === "total") {
      return <strong>{formatCurrency(payload[spec.name])}</strong>;
    }
    if (spec.name === "details") {
      return <Details transactions={payload.transactions} />;
    }
    return convertToDisplayValue(spec, payload);
  };

  const Details: FC<{ transactions: any[] }> = (detailsProps) => {
    const spec: ModelSpec[] = [
      {
        key: "description",
        label: "Description",
        name: "description",
        type: "string",
      },
      { key: "amount", label: "Amount", name: "amount", type: "currency" },
    ];
    const rows: AckTableRowProps[] = detailsProps.transactions.map(
      (value: any) => ({ spec: spec, payload: value })
    );
    const detailsFormatters: AckTableRowFormatter = (
      spec: ModelSpec,
      payload: any,
      _: number
    ): any => {
      if (spec.name === "amount") {
        return <strong>{formatCurrency(payload[spec.name])}</strong>;
      }
      return convertToDisplayValue(spec, payload);
    };
    return (
      <Accordion
        title={`Registered ${detailsProps.transactions.length} transactions`}
      >
        <AckTable rows={rows} formatters={detailsFormatters} />
      </Accordion>
    );
  };

  const Chart = () => {
    const labelFormatter = (data: any) => {
      // Recharts v2.x passes the data item in `payload`
      const expense = data.payload?.payload || data.payload || data;
      return `${expense?.name || ""} - ${formatCurrency(expense?.value || 0)}`;
    };
    return (
      <div style={{ width: "100%", height: 400 }}>
        <ResponsiveContainer>
          <PieChart margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
            <Pie
              data={data}
              nameKey="id"
              dataKey="value"
              strokeOpacity="0.1"
              label={labelFormatter}
            >
              {data.map((entry, index) => {
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                );
              })}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <>
      <h1 className="subtitle">Expenses Breakdown</h1>
      <AckErrorMessage errors={errors} />
      <AckLoader isLoading={isLoading} />
      {!isLoading && data.length > 0 && <Chart />}
      {!isLoading && rows.length > 0 && (
        <AckTable rows={rows} formatters={formatters} />
      )}
      {!isLoading && data.length === 0 && rows.length === 0 && <Empty />}
    </>
  );
};

const MonthlyBreakdownSummary = () => {
  const [months, setMonths] = useState([] as string[]);
  const [selectedMonth, setSelectedMonth] = useState(
    undefined as string | undefined
  );
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    const values = await ackFetch.get("monthly-transactions/");
    setMonths(values);
    if (values.length > 0) {
      setSelectedMonth(values[0]);
    }
  });

  const MonthSelector = () => {
    const onChange = (value: any) => setSelectedMonth(value);
    const optionsLabels = months.map((o, index) => (
      <option key={"option-" + index} value={o}>
        {o}
      </option>
    ));
    const className = isLoading
      ? "select is-fullwidth is-loading"
      : "select is-fullwidth";
    return (
      <div className="field mb-6">
        <div className="control">
          <div className={className}>
            <select
              value={selectedMonth}
              onChange={(e) => onChange(e.target.value)}
            >
              {optionsLabels}
            </select>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <AckErrorMessage errors={errors} />
      {months.length > 0 && <MonthSelector />}
      {months.length === 0 && <Empty />}
      {!isLoading && selectedMonth && <FundsSummary month={selectedMonth} />}
      {!isLoading && selectedMonth && (
        <ExpensesBreakdown month={selectedMonth} />
      )}
    </>
  );
};

export const MonthlySummary = () => {
  const progress = useProgress();
  return (
    <div className="section">
      <h1 className="title is-spaced">Monthly Summary</h1>
      {!progress.isComplete && <Progress {...progress} />}
      {progress.isComplete && <MonthlyBreakdownSummary />}
    </div>
  );
};
